import {AxiosInstance, AxiosRequestConfig} from "axios";
import Asset from "../model/asset/Asset";
import {CreateUpdateAssetApiDto} from "./model/rest";
import {isNullOrUndefined} from "util";
import AssetMaterialsBreakdown from "../model/asset/AssetMaterialsBreakdown";
import AssetHistory from "../model/asset/AssetHistory";
import AssetAttribute from "../model/assetAttribute/AssetAttribute";
import DictionaryCache from "../helper/DictionaryCache";
import {api} from "./api";
import {WorkspaceName} from "../components/v5/table/workspace/WorkspaceContainer";
import WildcardFilter, {WildcardFilterColumnType} from "../model/workspace/WildcardFilter";
import AssetSearch from "../model/asset/AssetSearch";
import {FilterTypeEnum} from "../model/workspace/Filter";
import {ColumnTypeEnum} from "../model/workspace/ColumnDefinition";
import {none} from "ts-option";
import download = require("downloadjs");
const qs = require('qs');

export default class AssetApi {
	/**
	 * Cache for searching assets.
	 * It prevents unnecessary request to the server.
	 */
	assetsCache: DictionaryCache<AssetSearch[]>;

	constructor(public readonly axiosInstance: AxiosInstance) {
		this.assetsCache = DictionaryCache.empty();
	}

	/**
	 * Fetch asset.
	 * @returns {Promise<Asset>} Promise containing asset.
	 */
	fetchAsset(assetId: string) {
		const url = `/api/web/assets/${assetId}`;
		return this.axiosInstance.get(url).then(resp => Asset.fromJson(resp.data));
	}

	/**
	 * Update asset
	 * @param {string} assetId
	 * @param {CreateUpdateAssetApiDto} data
	 * @returns {AxiosPromise<any>}
	 */
	updateAsset(assetId: string, data: CreateUpdateAssetApiDto) {
		const url = `/api/web/assets/${assetId}`;
		return this.axiosInstance.put(url, data);
	}

	/**
	 * Create asset
	 * @param {CreateUpdateAssetApiDto} data
	 * @returns {Promise<Asset>}
	 */
	createAsset(data: CreateUpdateAssetApiDto) {
		const url = `/api/web/assets`;
		return this.axiosInstance.post(url, data).then((resp) => Asset.fromJson(resp.data));
	}

	/**
	 * Download assets xlsx
	 * @param {string[]} assetHeadersList
	 * @returns {Promise<void>}
	 */
	downloadAssets(assetHeadersList?: string[]) {
		const url = `/api/web/assets/export`;
		const query: AxiosRequestConfig = {responseType: "blob"};
		if (assetHeadersList && assetHeadersList.length > 0) {
			query.params = {headers: assetHeadersList.join(',')};
		}
		return this.axiosInstance.get(url, query).then((resp) => {
			const disposition = resp.headers['content-disposition'];
			const filename = disposition.match(/filename=(.*)/)[1];
			download(resp.data, isNullOrUndefined(filename) ? "assets.xlsx" : filename);
		});
	}

	/**
	 * Upload assets xlsx
	 * @param {string} assetAttributeHeader
	 * @param {File} file
	 */
	uploadAssets(file: File, assetAttributeHeader: string) {
		const url = `/api/web/assets/import`;
		const config: AxiosRequestConfig = {headers: {'content-type': 'multipart/form-data'}};
		if (assetAttributeHeader) {
			config.params = {header: assetAttributeHeader};
		}
		const formData = new FormData();
		formData.append('file', file);
		return this.axiosInstance.post(url, formData, config)
	}

	/**
	 * Find assets by text.
	 * @param text text using to search.
	 * @param excludeIds
	 * @returns promise with results.
	 */
	findAssetsByText(text: string, excludeIds: string[] = []): Promise<AssetSearch[]> {
		const cacheKey = text + excludeIds.join(",");
		return new Promise((resolve, reject) => {
			const cachedData = this.assetsCache.get(cacheKey);
			if (cachedData.isDefined)
				resolve(cachedData.get);
			else {
				const filters = [new WildcardFilter(text).toQueryJson(WildcardFilterColumnType.Assets),
					{
						propertyName: "disabled",
						operatorType: FilterTypeEnum.Equal,
						values: ["false"],
						columnType: ColumnTypeEnum.StatusBoolean
					}];
				if (excludeIds.length) {
					filters.push({
						columnType: ColumnTypeEnum.Uuid,
						operatorType: FilterTypeEnum.NotIn,
						propertyName: "id",
						values: excludeIds
					})
				}

				api.instance.workspaceApi.fetchWorkspaceData(WorkspaceName.Assets, 1,
					filters,
					['manufacturer,asc', 'model,asc', 'id,asc'], 999).then(resp => {
					const data = resp.items.map(AssetSearch.fromJson);
					this.assetsCache.store(cacheKey, data);
					resolve(data);
				}).catch(reject);
			}
		});
	}

	/**
	 * Find assets for quote
	 * @param text
	 * @param potentialPrices
	 * @param excludeIds
	 * @param brandId
	 * @param partnerBrandId
	 */
	findAssetsForQuote(text: string,
		potentialPrices: number[],
		excludedAssetIds: string[] = [],
		brandId: string,
		partnerBrandId?: string): Promise<AssetSearch[]> {
		const url = `/api/web/assets/search`
		const data = {text, potentialPrices, brandId, partnerBrandId, excludedAssetIds};
		const params = {
			params: {page: 0, size: 999, sort: ['manufacturer,asc', 'model,asc', 'id,asc']},
			paramsSerializer: (params: string[]) => qs.stringify(params, {arrayFormat: 'repeat'})
		};
		return this.axiosInstance.post(url, data, params).then((resp) => resp.data.items.map(AssetSearch.fromJson));
	}

	/**
	 * Fetch Asset search by ids
	 * @param assetIds
	 */
	fetchAssetsByIds(assetIds: string[]): Promise<AssetSearch[]> {
		const filters = [{
			columnType: ColumnTypeEnum.Uuid,
			operatorType: FilterTypeEnum.In,
			propertyName: "id",
			values: assetIds
		}]
		return api.instance.workspaceApi.fetchWorkspaceData(WorkspaceName.Assets, 1, filters, ['id,asc'], 999)
			.then(resp => resp.items.map(AssetSearch.fromJson))
	}

	/**
	 * Clear assets search cache.
	 */
	clearAssetsCache() {
		this.assetsCache = DictionaryCache.empty();
	}

	/**
	 * Add attachment to asset
	 * @param {string} assetId
	 * @param ids
	 * @returns {AxiosPromise<any>}
	 */
	addOrUpdateAttachmentIds(assetId: string, ids: string[]) {
		const url = `/api/web/asset/${assetId}/images`;
		return this.axiosInstance.post(url, {ids});
	}

	/**
	 * Fetch asset materials breakdown divided by merchants
	 * @param {string} assetId
	 * @returns {AxiosPromise<MaterialsBreakdown[]>}
	 */
	fetchAssetMaterialsBreakdown(assetId: string): Promise<AssetMaterialsBreakdown[]> {
		const url = `/api/web/assets/${assetId}/materials`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(AssetMaterialsBreakdown.fromJson));
	}

	/**
	 * Fetch asset history
	 * @param {string} assetId
	 * @returns {AxiosPromise<AssetHistory[]>}
	 */
	fetchAssetHistory(assetId: string): Promise<AssetHistory[]> {
		const url = `/api/web/assets/${assetId}/revisions`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(AssetHistory.fromJson));
	}

	/**
	 * Fetch asset attributes
	 * @returns {Promise<AssetAttribute[]>}
	 */
	fetchAssetAttributes(): Promise<AssetAttribute[]> {
		const url = '/api/web/assets/attributes';
		return this.axiosInstance.get(url).then(resp => resp.data.map(AssetAttribute.fromJson));
	}
}
